import React, { useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useRouter } from "utils";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import { Page, PageContent, PageHeader, ScrollToTopOnMount } from "frame/components";
import NotFound from "./not-found";
import EquipmentSummary from "./equipment-summary-container";
import EquipmentTimesheetsPanel from "./equipment-timesheets-panel";
import EquipmentUsagePanel from "./equipment-usage-panel";
import fromTypes from "../actions/types";

const useStyles = makeStyles((theme) => ({
	root: {},
	sectionSlot: {
		margin: theme.spacing(2, 2, 0, 2),
		[theme.breakpoints.down("xs")]: {
			margin: theme.spacing(1, 1.5, 0, 1.5),
		},
	},
}));

const SECTION_EQUIPMENT_INFO = "equipmentInfo";
const SECTION_USAGE = "usage";
const SECTION_TIMESHEETS = "timesheets";

const EquipmentDetail = ({ equipment }) => {
	const classes = useStyles();
	const { history } = useRouter();
	const equipmentUsageRecordingEnabled = useSelector(
		({ featureFlags }) =>
			featureFlags?.equipment === true &&
			featureFlags?.equipmentUsageRecording === true
	);
	const [expandedSection, setExpandedSection] = useState(
		SECTION_EQUIPMENT_INFO
	);

	const onEquipmentInfoExpanded = useCallback((event, isExpanded) => {
		setExpandedSection(isExpanded ? SECTION_EQUIPMENT_INFO : null);
	}, []);

	const onUsageExpanded = useCallback((event, isExpanded) => {
		setExpandedSection(isExpanded ? SECTION_USAGE : null);
	}, []);

	const onTimesheetsExpanded = useCallback((event, isExpanded) => {
		setExpandedSection(isExpanded ? SECTION_TIMESHEETS : null);
	}, []);

	const name = (equipment && equipment.equipment) || "Not Found";
	const path =
		equipment && equipment.id
			? `${fromTypes.EQUIPMENT_ROUTE}/${equipment.id}`
			: fromTypes.EQUIPMENT_ROUTE;

	const breadcrumbs = [
		{
			label: `Equipment`,
			value: fromTypes.EQUIPMENT_ROUTE,
		},
		{
			label: `${name}`,
			value: path,
		},
	];

	return (
		<Page className={classes.root} title="Equipment">
			<PageHeader breadcrumbs={breadcrumbs} title={name} />
			{equipment ? (
				<PageContent>
					<ScrollToTopOnMount />
					<div className={classes.sectionSlot}>
						<EquipmentSummary
							expanded={
								expandedSection === SECTION_EQUIPMENT_INFO
							}
							onExpandedChange={onEquipmentInfoExpanded}
						/>
					</div>
					<div className={classes.sectionSlot}>
						<EquipmentUsagePanel
							equipmentId={equipment.id}
							expanded={expandedSection === SECTION_USAGE}
							onExpandedChange={onUsageExpanded}
							onRecordUsage={
								equipmentUsageRecordingEnabled &&
								equipment.maintenanceType !== "Month"
									? () =>
											history.push(
												`${fromTypes.EQUIPMENT_USAGE_ADD_ROUTE}?equipmentId=${equipment.id}`,
												{
													from: `${fromTypes.EQUIPMENT_ROUTE}/${equipment.id}`,
												}
											)
									: undefined
							}
						/>
					</div>
					<div className={classes.sectionSlot}>
						<EquipmentTimesheetsPanel
							equipmentId={equipment.id}
							expanded={expandedSection === SECTION_TIMESHEETS}
							onExpandedChange={onTimesheetsExpanded}
						/>
					</div>
				</PageContent>
			) : (
				<PageContent pad>
					<NotFound />
				</PageContent>
			)}
		</Page>
	);
};

EquipmentDetail.propTypes = {
	equipment: PropTypes.object,
};

export default EquipmentDetail;
